import { useEffect, useMemo, useState } from 'react';
import { Building2, User, Calendar } from 'lucide-react';
import {
  useTenantUsers,
  useUpdateAuditMetadata,
  type AuditDetail,
} from '../../lib/api/queries';

/**
 * Editable metadata strip: Organisation (client being audited), Assessor, and
 * Assessment Date. Text fields debounce; the date picker fires immediately
 * because each change is discrete.
 *
 * When the lock isn't held the inputs go read-only — the auditor can still
 * see the values but can't change them.
 *
 * Slice 14+ — Organisation is stamped from a real ClientOrganisation row
 * at audit-create time, so the input is always read-only here. Renaming
 * the client happens on the /organisations admin page, not per-audit.
 */
export function AuditMetadataHeader({
  tenantId,
  audit,
  editable,
}: {
  tenantId: string;
  audit: AuditDetail;
  editable: boolean;
}) {
  const update = useUpdateAuditMetadata(tenantId, audit.id);
  const usersQuery = useTenantUsers();

  // Mirror server values into local state so we can debounce the network
  // call without making the inputs feel laggy. Re-sync when the audit's
  // server state changes (e.g. SignalR push or another tab edit).
  const [assessor,  setAssessor]  = useState(audit.assessorName     ?? '');
  const [date,      setDate]      = useState(
    audit.assessmentDate ? audit.assessmentDate.slice(0, 10) : '');

  useEffect(() => { setAssessor(audit.assessorName ?? ''); }, [audit.assessorName]);
  useEffect(() => {
    setDate(audit.assessmentDate ? audit.assessmentDate.slice(0, 10) : '');
  }, [audit.assessmentDate]);

  // Assessor option list — current tenant users by display name, plus the
  // audit's existing value if it doesn't match any current user (handles
  // imported audits or users who've been removed). Both value and label
  // use the display name; we don't track the user id on the audit because
  // assessor identities may outlive a user account.
  const assessorOptions = useMemo(() => {
    const users = usersQuery.data ?? [];
    const names = users.map(u => u.displayName);
    if (assessor && !names.includes(assessor)) {
      return [assessor, ...names];
    }
    return names;
  }, [usersQuery.data, assessor]);

  const onAssessorChange = (v: string) => {
    setAssessor(v);
    // Discrete change — fire immediately, no debounce.
    update.mutate({ assessorName: v.length > 0 ? v : null });
  };
  const onDateChange = (v: string) => {
    setDate(v);
    // Empty string -> null on the server (clears the date).
    update.mutate({ assessmentDate: v ? new Date(v).toISOString() : null });
  };

  const inputCls =
    'w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm ' +
    'focus:outline-none focus:ring-2 focus:ring-accent-alt-500 focus:border-transparent ' +
    'disabled:bg-gray-50 disabled:cursor-not-allowed';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-white border border-gray-200 rounded-lg p-4">
      <Field label="Organisation" icon={<Building2 className="w-4 h-4 text-gray-400" aria-hidden />}>
        <input
          type="text"
          value={audit.organisationName ?? ''}
          readOnly
          disabled
          title="Set at audit creation; rename the client on the Organisations page."
          placeholder="Client organisation"
          className={inputCls}
        />
      </Field>
      <Field label="Assessor" icon={<User className="w-4 h-4 text-gray-400" aria-hidden />}>
        <select
          value={assessor}
          onChange={e => onAssessorChange(e.target.value)}
          disabled={!editable || usersQuery.isLoading}
          className={inputCls + ' pr-9 appearance-none bg-white'}
        >
          <option value="">— select assessor —</option>
          {assessorOptions.map(email => (
            <option key={email} value={email}>{email}</option>
          ))}
        </select>
      </Field>
      <Field label="Assessment date" icon={<Calendar className="w-4 h-4 text-gray-400" aria-hidden />}>
        <input
          type="date"
          value={date}
          onChange={e => onDateChange(e.target.value)}
          disabled={!editable}
          className={inputCls}
        />
      </Field>
    </div>
  );
}

function Field({
  label, icon, children,
}: { label: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {icon}
        </span>
        {children}
      </div>
    </label>
  );
}
